import React, { FC, useMemo } from "react";
import { Link } from "react-router-dom";
import ArrowIcon from "../../icons/ArrowIcon";
import DateInputGradient from "../../atoms/DateInputGradient/DateInputGradient";
import { periods } from "./statisticsConfig";
import { formatValue } from "./statisticsUtils";

interface SalarySectionProps {
    stats: any;
    linkTo: string;
    colorConfig: { textColor: string; color: string };
    salaryDateFrom: string;
    salaryDateTo: string;
    setSalaryDateFrom: (v: string) => void;
    setSalaryDateTo: (v: string) => void;
    onReset: () => void;
}

const SalarySection: FC<SalarySectionProps> = ({
                                                   stats,
                                                   linkTo,
                                                   colorConfig,
                                                   salaryDateFrom,
                                                   salaryDateTo,
                                                   setSalaryDateFrom,
                                                   setSalaryDateTo,
                                                   onReset
                                               }) => {
    // 1. Логика периодов
    const { mainPeriods, allTimePeriod } = useMemo(() => {
        const norm = (v: any) => String(v ?? "").toLowerCase();

        const all =
            periods.find((p: any) => {
                const k = norm(p.key);
                const l = norm(p.label);
                return (
                    ["all", "all_time", "alltime", "total", "lifetime"].includes(k) ||
                    /все|всё|all\s*time|lifetime/i.test(l)
                );
            }) || null;

        const pick = (rx: RegExp, keyFallback?: string) => {
            const byKey = keyFallback ? periods.find((p: any) => norm(p.key) === norm(keyFallback)) : null;
            if (byKey && (!all || byKey.key !== all.key)) return byKey;

            const byLabel = periods.find((p: any) => rx.test(norm(p.label)) && (!all || p.key !== all.key));
            return byLabel || null;
        };

        const desired = [pick(/день|day/i, "day"), pick(/недел|week/i, "week"), pick(/месяц|month/i, "month"), pick(/год|year/i, "year")].filter(
            Boolean
        ) as any[];

        const dedup = Array.from(new Map(desired.map((p: any) => [String(p.key), p])).values());
        const fallbackMain = periods.filter((p: any) => (!all || p.key !== all.key));

        return {
            mainPeriods: dedup.length ? dedup : fallbackMain,
            allTimePeriod: all
        };
    }, []);

    const renderSalaryItemsDefault = () => {
        const byName = stats?.salary?.byName || {};
        const allNamesSet = new Set<string>();

        Object.keys(byName).forEach(periodKey => {
            const list = byName[periodKey] || [];
            list.forEach((item: any) => {
                if (item.name) allNamesSet.add(item.name);
            });
        });

        const allNames = Array.from(allNamesSet);
        if (!allNames.length) return null;

        return allNames.map(name => {
            const values: Record<string, number> = {};

            periods.forEach(period => {
                const list = byName[period.key] || [];
                const found = list.find((item: any) => item.name === name);
                values[period.key] = found?.totalRubAmount || 0;
            });

            const mainValue = values["month"] || 0;

            return (
                <div className="statistics-items__item" key={`salary-${name}`}>
                    <Link to={linkTo} className="statistics-items__item-link">
                        <ArrowIcon />
                    </Link>
                    <div className="statistics-items__item-title">Зарплата: {name}</div>
                    <div className="statistics-items__item-list">
                        <div className="statistics-items__item-content">
                            <div className="statistics-items__item-content-counter statistics-items__item-content-counter--column">
                                <div className="statistics-items__item-content-counter-main">
                                    <span>{formatValue(mainValue, true, 'USDT')}</span>
                                    <span>
                                        <b>за месяц →</b> {name}
                                    </span>
                                </div>

                                <div className="statistics-items__item-content-counter-total statistics-items__item-content-counter-total--row">
                                    <div className="statistics-items__periods">
                                        <div className="statistics-items__periods-grid">
                                            {mainPeriods.map((period: any) => (
                                                <div
                                                    key={`salary-${name}-${period.key}`}
                                                    className="is-green"
                                                >
                                                    {formatValue(values[period.key] || 0, true, 'USDT')}
                                                    <span>{period.label}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {allTimePeriod && (
                                            <div className="statistics-items__periods-all">
                                                <div
                                                    key={`salary-${name}-${allTimePeriod.key}`}
                                                    className="is-green"
                                                >
                                                    {formatValue(values[allTimePeriod.key] || 0, true, 'USDT')}
                                                    <span>{allTimePeriod.label}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };

    const renderSalaryItemsRange = () => {
        const list = stats?.salary?.range?.byName || [];
        if (!Array.isArray(list) || !list.length) return null;

        const label = salaryDateFrom || salaryDateTo ? `${salaryDateFrom || "…"} — ${salaryDateTo || "…"}` : "…";

        return list.map((row: any) => {
            const name = row?.name || "Не указано";
            const v = Number(row?.totalRubAmount || 0);

            return (
                <div className="statistics-items__item" key={`salary-range-${name}`}>
                    <Link to={linkTo} className="statistics-items__item-link">
                        <ArrowIcon />
                    </Link>
                    <div className="statistics-items__item-title">Зарплата: {name} (за период)</div>
                    <div className="statistics-items__item-list">
                        <div className="statistics-items__item-content">
                            <div className="statistics-items__item-content-counter">
                                <div className="statistics-items__item-content-counter-main">
                                    <span>{formatValue(v, true, 'USDT')}</span>
                                    <span>
                                        <b>период →</b> {label}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };

    const hasSalaryRange = !!(salaryDateFrom || salaryDateTo) && !!stats?.salary?.range;

    return (
        <>
            <div className="statistics-items__filter">
                <div className="statistics-items__filter-row">
                    <div className="statistics-items__filter-field">
                        <DateInputGradient
                            name="salaryDateFrom"
                            placeholder="Дата с"
                            value={salaryDateFrom}
                            onChange={v => setSalaryDateFrom(v)}
                        />
                    </div>
                    <div className="statistics-items__filter-field">
                        <DateInputGradient
                            name="salaryDateTo"
                            placeholder="Дата по"
                            value={salaryDateTo}
                            onChange={v => setSalaryDateTo(v)}
                        />
                    </div>
                    <button type="button" className="statistics-items__transfer-tab" onClick={onReset}>
                        Сбросить
                    </button>
                </div>
            </div>

            <div className="statistics-items__list">{hasSalaryRange ? renderSalaryItemsRange() : renderSalaryItemsDefault()}</div>
        </>
    );
};

export default SalarySection;